import React, { useState, useEffect, useMemo } from "react";
import { Formik, Form, Field, ErrorMessage } from "formik";
import * as Yup from "yup";
import "./AddRecruitment.css";
import config from "../../config";
import SuccessPopup from "../../components/common/SuccessPopup";
import ErrorPopup from "../../components/common/ErrorPopup";
import { VscCloudDownload } from "react-icons/vsc";
import { LuPencilLine } from "react-icons/lu";
import QRCode from "react-qr-code";


const AddRecruitment = ({
  onCancel,
  onSave,
  selectedRecruitment,
  onDelete,
}) => {
  const [qrValue, setQrValue] = useState("");
  const today = new Date().toISOString().split("T")[0];
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [errorMsg, setErrorMsg] = useState("");

  const isEditMode = !!selectedRecruitment;
  const [isViewMode, setIsViewMode] = useState(isEditMode);

  // Clients from API: [{ name, clientKey }]
  const [clients, setClients] = useState([]);

  // Job Roles & Qualifications from Config API (values)
  const [jobRoles, setJobRoles] = useState([]);
  const [qualifications, setQualifications] = useState([]);

  const [userDetails, setUserDetails] = useState({
    name: "",
    role: "",
    token: "",
    orgId: "",
  });

  const pocList = ["Ramesh", "Priya", "Vijay"];

  const [successPopup, setSuccessPopup] = useState(null);
  const [errorPopup, setErrorPopup] = useState(null);

  useEffect(() => {
    // Use recruitmentId if available (Edit/View mode), otherwise fallback to empty string for new ones
    const recruitmentId = selectedRecruitment?.id || "";

    const dynamicUrl =
      `https://docs.google.com/forms/d/e/1FAIpQLSeOz0Rq3cH0lQqIBD3eecSEfJ7dOfpLUjRXr8OXCi-iCYve0Q/viewform?usp=pp_url&entry.1931733153=${encodeURIComponent(recruitmentId)}`;
    setQrValue(dynamicUrl);
  }, [selectedRecruitment]);
  // Grab auth/user context
  useEffect(() => {
    const token = localStorage.getItem("Authorization") || "";
    const firstName = localStorage.getItem("firstName") || "";
    const lastName = localStorage.getItem("lastName") || "";
    const role = localStorage.getItem("role")?.toUpperCase() || "GUEST";
    const orgId = localStorage.getItem("orgId") || "";

    setUserDetails({
      name: `${firstName} ${lastName}`.trim(),
      role,
      token,
      orgId,
    });
  }, []);

  // Fetch CLIENTS
  useEffect(() => {
    if (!userDetails.token || !userDetails.orgId) return;

    const fetchClients = async () => {
      setLoading(true);
      try {
        const response = await fetch(
          `${config.API_BASE_URL}${config.CLIENT_LIST_ENDPOINT}${userDetails.orgId}`,
          {
            method: "GET",
            headers: {
              Authorization: `Bearer ${userDetails.token}`,
              "Content-Type": "application/json",
            },
            credentials: "include",
          }
        );

        if (!response.ok) {
          throw new Error(`Failed to fetch clients (HTTP ${response.status})`);
        }

        const data = await response.json();
        const normalized = Array.isArray(data.body)
          ? data.body
            .filter((c) => c && typeof c.name === "string")
            .map((c) => ({
              name: c.name,
              clientKey: c.clientKey,
              primaryContactName: c.primaryContactname || c.primaryContactName || "",
              primaryContactRole: c.role || c.primaryContactRole || ""
            }))
          : [];

        setClients(normalized);
        setError(null);
      } catch (err) {
        console.error("Client fetch error:", err);
        setError("Could not load clients.");
        setClients([]);
      } finally {
        setLoading(false);
      }
    };

    fetchClients();
  }, [userDetails.token, userDetails.orgId]);

  // Generic config fetcher — reuse for JobRole & Qualification
  const fetchRoleDropdownOptions = async (type, setter) => {
    try {
      const response = await fetch(
        `${config.API_BASE_URL}${config.VIEW_CLIENT_CONFIG_ENDPOINT}${userDetails.orgId}?configType=${type}`,
        {
          headers: {
            Authorization: `Bearer ${userDetails.token}`,
            "Content-Type": "application/json",
          },
          credentials: "include",
        }
      );
      if (!response.ok) throw new Error("Failed to fetch " + type);
      const data = await response.json();

      const activeItems = (Array.isArray(data) ? data : []).filter(
        (item) => item && item.isActive
      );

      const valuesList = activeItems
        .map((item) =>
          typeof item.values === "string" ? item.values.trim() : ""
        )
        .filter(Boolean);

      const unique = [...new Set(valuesList)];

      setter(unique);
    } catch (err) {
      console.error(`Error fetching ${type}:`, err);
    }
  };

  useEffect(() => {
    if (!userDetails.token || !userDetails.orgId) return;
    fetchRoleDropdownOptions("JobRole", setJobRoles);
    fetchRoleDropdownOptions("Qualification", setQualifications);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userDetails.token, userDetails.orgId]);

  // validation
  const validationSchema = Yup.object({
    recruitmentName: Yup.string()
      .required("Recruitment Name is required.")
      .max(150, "Recruitment Name cannot exceed 150 characters"),

    client: Yup.string().required("Client is required."),
    jobRole: Yup.string().required("Job Role is required."),
    interviewPlace: Yup.string().nullable(),

    description: Yup.string()
      .required("Description is required.")
      .max(500, "Description cannot exceed 500 characters").nullable(),

    remarks: Yup.string()
      .max(300, "Remarks cannot exceed 300 characters")
      .nullable(),

    pocName: Yup.string().required("Point Of Contact is required."),
    pocRole: Yup.string()
      .required("POC Role is required.")
      .max(21, "POC Role must be at most 21 characters")
      .matches(/^[A-Za-z0-9 ]+$/, "Only letters, numbers and spaces allowed"),
    qualification: Yup.string().required("Qualification is required."),
    vacancy: Yup.number()
      .typeError("Vacancy must be a number")
      .positive()
      .integer()
      .required("Vacancy is required."),
    startDate: Yup.date()
      .min(new Date(), "Past dates are not allowed")
      .required("Start Date is required."),
    endDate: Yup.date()
      .nullable()
      .min(Yup.ref("startDate"), "End Date must be after Start Date")
      .min(new Date(), "Past dates are not allowed"),
    skills: Yup.string()
      .max(30, "Skills cannot exceed 30 characters")
      .nullable(),

  });

  // blank initial values
  const blankInitialValues = {
    recruitmentName: "",
    client: "",
    jobRole: "",
    interviewPlace: "",
    description: "",
    remarks: "",
    pocName: "",
    pocRole: "",
    qualification: "",
    vacancy: "",
    startDate: "",
    endDate: "",
    skills: "",
  };

  // map selectedRecruitment → form initial values
  const initialValues = useMemo(() => {
    if (!isEditMode || !selectedRecruitment) {
      return blankInitialValues;
    }

    return {
      recruitmentName: selectedRecruitment.recruitmentName || "",
      client: selectedRecruitment.clientName || "",
      jobRole: selectedRecruitment.jobRole || "",
      interviewPlace: selectedRecruitment.interviewPlace || "",
      description: selectedRecruitment.description || "",
      remarks: selectedRecruitment.remarks || "",
      pocName: selectedRecruitment.pointOfContact || "",
      pocRole: selectedRecruitment.pointOfContactRole || "",
      qualification: selectedRecruitment.qualification || "",
      vacancy:
        selectedRecruitment.vacancy != null
          ? String(selectedRecruitment.vacancy)
          : "",
      startDate: selectedRecruitment.startDate || "",
      endDate: selectedRecruitment.endDate || "",
      skills: selectedRecruitment.skills || "",
    };
  }, [isEditMode, selectedRecruitment]);

  // submit (create + update)
  const handleSubmit = async (values, { setSubmitting, resetForm }) => {
    const requestBody = {
      recruitmentName: values.recruitmentName?.trim(),
      clientName: values.client?.trim(),
      jobRole: values.jobRole?.trim(),
      interviewPlace: values.interviewPlace?.trim(),
      description: values.description?.trim(),
      remarks: values.remarks?.trim() || "",
      pointOfContact: values.pocName?.trim(),
      pointOfContactRole: values.pocRole?.trim(),
      qualification: values.qualification?.trim(),
      skills: values.skills?.trim(),
      vacancy: Number(values.vacancy),
      startDate: values.startDate,
      endDate: values.endDate,
      status: selectedRecruitment?.status || "Open",
      orgKey: Number(userDetails.orgId),
    };

    const isEdit = !!selectedRecruitment;
    const id =
      selectedRecruitment && selectedRecruitment.id != null
        ? selectedRecruitment.id
        : null;

    const baseUrl = `${config.API_BASE_URL}${config.ADD_RECRUITMENT_ENDPOINT}`;
    const url = isEdit && id != null
      ? `${baseUrl}/${id}?orgKey=${userDetails.orgId}`
      : `${baseUrl}?orgKey=${userDetails.orgId}`;
    const method = isEdit && id != null ? "PUT" : "POST";

    try {
      const response = await fetch(url, {
        method,
        headers: {
          Authorization: `Bearer ${userDetails.token}`,
          "Content-Type": "application/json",
        },
        body: JSON.stringify(requestBody),
        credentials: "include",
      });

      const text = await response.text();
      let data = null;
      try {
        data = JSON.parse(text);
      } catch {
        // ignore parse error
      }

      if (response.ok) {
        setSuccessPopup(
          isEdit
            ? "Recruitment updated successfully!"
            : "Recruitment created successfully!"
        );

        if (onSave) onSave(data || values);
        if (!isEdit) {
          resetForm();
        }
      } else {
        const msg =
          data ||
          text ||
          "Recruitment request failed. Please check your input and try again.";
        setErrorPopup(
          typeof msg === "string"
            ? msg
            : `Request failed: ${JSON.stringify(msg)}`
        );
      }
    } catch (err) {
      console.error("Network/server error creating/updating recruitment:", err);
      setErrorPopup("Something went wrong. Please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  // DELETE uses **id** now
  const handleDelete = async () => {
    if (!selectedRecruitment) {
      alert("No recruitment selected to delete.");
      return;
    }

    const id =
      selectedRecruitment && selectedRecruitment.id != null
        ? String(selectedRecruitment.id)
        : null;

    if (!id) {
      alert("No valid recruitment id found for this recruitment.");
      return;
    }

    const confirmDelete = window.confirm(
      "Are you sure you want to delete this recruitment?"
    );
    if (!confirmDelete) return;

    const url = `${config.API_BASE_URL}${config.ADD_RECRUITMENT_ENDPOINT}/${id}`;
    console.log("Deleting recruitment at URL:", url);

    try {
      const response = await fetch(url, {
        method: "DELETE",
        headers: {
          Authorization: `Bearer ${userDetails.token}`,
          "Content-Type": "application/json",
        },
        credentials: "include",
      });

      const text = await response.text();
      console.log("Delete response text:", text);

      if (!response.ok) {
        setErrorMsg(`Unexpected response: ${text}`);
        setErrorPopup(`Unexpected response: ${text}`);
        return;
      }

      setSuccessPopup("Recruitment deleted successfully!");
      if (onDelete) {
        onDelete(selectedRecruitment);
      }
    } catch (err) {
      console.error("Delete recruitment error:", err);
      setErrorMsg("Failed to delete recruitment. Please try again.");
      setErrorPopup("Failed to delete recruitment. Please try again.");
    }
  };

  const isReadOnly = isViewMode;

  return (
    <div>
      {successPopup && (
        <SuccessPopup
          message={successPopup}
          onClose={() => {
            setSuccessPopup(null);
            if (onCancel) onCancel();
          }}
        />
      )}

      {errorPopup && (
        <ErrorPopup message={errorPopup} onClose={() => setErrorPopup(null)} />
      )}

      <div className='addrec-content-row'>
        <div className='addrec-left-pane'>
          <Formik
            enableReinitialize
            initialValues={initialValues}
            validationSchema={validationSchema}
            onSubmit={handleSubmit}
          >
            {({ isSubmitting, setFieldValue }) => (
              <div className='addrec-scroll-area'>
                {/* IMPORTANT: button bar moved INSIDE <Form>  */}
                <Form className='add-recruitment-form'>
                  {/* Recruitment Name */}
                  <div className='input-group'>
                    <label>
                      Recruitment Name{" "}
                      <span className='required-asterisk'>*</span>
                    </label>
                    <Field
                      type='text'
                      name='recruitmentName'
                      disabled={isReadOnly}
                    />
                    <ErrorMessage
                      name='recruitmentName'
                      component='div'
                      className='error'
                    />
                  </div>

                  {/* Client + Job Role */}
                  <div className='input-row'>
                    <div className='input-group half-width'>
                      <label>Client</label>
                      <Field
                        as='select'
                        name='client'
                        disabled={isReadOnly || loading || !!error}
                        onChange={(e) => {
                          const selectedName = e.target.value;
                          setFieldValue("client", selectedName);

                          // Auto-fill POC details from selected client
                          const clientData = clients.find(c => c.name === selectedName);
                          if (clientData) {
                            setFieldValue("pocName", clientData.primaryContactName || "");
                            setFieldValue("pocRole", clientData.primaryContactRole || "");
                          }
                        }}
                      >
                        <option value=''>Select
                          {loading
                            ? "Loading clients..."
                            : error
                              ? "Failed to load clients"
                              : ""}
                        </option>
                        {!loading &&
                          !error &&
                          clients.map((c) => (
                            <option key={c.clientKey ?? c.name} value={c.name}>
                              {c.name}
                            </option>
                          ))}
                      </Field>
                      <ErrorMessage
                        name='client'
                        component='div'
                        className='error'
                      />
                    </div>

                    <div className='input-group half-width'>
                      <label>Job Role</label>
                      <Field as='select' name='jobRole' disabled={isReadOnly}>
                        <option value=''>Select</option>
                        {jobRoles.map((role) => (
                          <option key={role} value={role}>
                            {role}
                          </option>
                        ))}
                      </Field>
                      <ErrorMessage
                        name='jobRole'
                        component='div'
                        className='error'
                      />
                    </div>
                  </div>

                  {/* Interview Place */}
                  <div className='input-group'>
                    <label>
                      Interview Place{" "}
                      <span className='required-asterisk'>*</span>
                    </label>
                    <Field
                      type='text'
                      name='interviewPlace'
                      disabled={isReadOnly}
                    />
                    <ErrorMessage
                      name='interviewPlace'
                      component='div'
                      className='error'
                    />
                  </div>

                  {/* Description */}
                  {/* Description */}
                  <div className='input-group'>
                    <label>
                      Description <span className='required-asterisk'>*</span>
                    </label>
                    <Field
                      type='text'
                      name='description'
                      disabled={isReadOnly}
                      maxLength={500}
                    />
                    <ErrorMessage
                      name='description'
                      component='div'
                      className='error'
                    />
                  </div>

                  {/* Remarks */}
                  <div className='input-group'>
                    <label>
                      Remarks <span className='required-asterisk'>*</span>
                    </label>
                    <Field
                      type='text'
                      name='remarks'
                      disabled={isReadOnly}
                      maxLength={300}
                    />
                    <ErrorMessage
                      name='remarks'
                      component='div'
                      className='error'
                    />
                  </div>

                  {/* POC */}
                  <div className='input-row'>
                    <div className='input-group half-width'>
                      <label>
                        Point Of Contact <span className='required-asterisk'>*</span>
                      </label>
                      <Field
                        type='text'
                        name='pocName'
                        disabled={isReadOnly}
                        placeholder=''
                      />
                      <ErrorMessage
                        name='pocName'
                        component='div'
                        className='error'
                      />
                    </div>
                    <div className='input-group half-width'>
                      <label>
                        Point Of Contact - Role
                        <span className='required-asterisk'>*</span>
                      </label>
                      <Field type='text' name='pocRole' disabled={isReadOnly} maxLength={21} />
                      <ErrorMessage
                        name='pocRole'
                        component='div'
                        className='error'
                      />
                    </div>
                  </div>

                  {/* Qualification + Vacancy */}
                  <div className='input-row'>
                    <div className='input-group half-width'>
                      <label>Qualification</label>
                      <Field
                        as='select'
                        name='qualification'
                        disabled={isReadOnly}
                      >
                        <option value=''>Select</option>
                        {qualifications.map((q) => (
                          <option key={q} value={q}>
                            {q}
                          </option>
                        ))}
                      </Field>
                      <ErrorMessage
                        name='qualification'
                        component='div'
                        className='error'
                      />
                    </div>

                    <div className='input-group half-width'>
                      <label>
                        Vacancy <span className='required-asterisk'>*</span>
                      </label>
                      <Field type='text' name='vacancy' disabled={isReadOnly} />
                      <ErrorMessage
                        name='vacancy'
                        component='div'
                        className='error'
                      />
                    </div>
                  </div>

                  {/* Dates */}
                  <div className='input-row'>
                    <div className='input-group half-width'>
                      <label>Start Date</label>
                      <Field
                        type='date'
                        name='startDate'
                        min={today}
                        disabled={isReadOnly}
                      />
                      <ErrorMessage
                        name='startDate'
                        className='error'
                        component='div'
                      />
                    </div>

                    <div className='input-group half-width'>
                      <label>End Date</label>
                      <Field
                        type='date'
                        name='endDate'
                        min={today}
                        disabled={isReadOnly}
                      />
                      <ErrorMessage
                        name='endDate'
                        className='error'
                        component='div'
                      />
                    </div>
                  </div>

                  {/* Skills */}
                  <div className='input-row'>
                    <div className='input-group half-width'>
                      <label>
                        Skills <span className='required-asterisk'>*</span>
                      </label>
                      <Field
                        type='text'
                        name='skills'
                        disabled={isReadOnly}
                        maxLength={30}
                      />
                      <ErrorMessage
                        name='skills'
                        component='div'
                        className='error'
                      />
                    </div>
                  </div>

                  {/* BUTTON BAR now INSIDE the form */}
                  <div className='button-container'>
                    <button
                      className='cancel-btn'
                      onClick={onCancel}
                      type='button'
                    >
                      {isViewMode ? "Back" : "Cancel"}
                    </button>

                    {!isViewMode && (
                      <button
                        className='save-btn'
                        type='submit'
                        disabled={isSubmitting}
                      >
                        {isSubmitting
                          ? isEditMode
                            ? "Updating..."
                            : "Saving..."
                          : isEditMode
                            ? "Update"
                            : "Add"}
                      </button>
                    )}
                  </div>
                </Form>
              </div>
            )}
          </Formik>
        </div>

        {/* RIGHT SIDE QR CARD + edit/delete */}
        <div className='addrec-right-pane'>
          {isEditMode && (
            <div className='client-form-action-bar'>
              <button
                type='button'
                className='client-delete-btn'
                onClick={handleDelete}
              >
                <span className='client-delete-icon'>🗑</span>
                <span>Delete</span>
              </button>

              <button
                type='button'
                className='client-edit-btn'
                onClick={() => setIsViewMode(!isViewMode)}
              >
                <span className='client-edit-icon'><LuPencilLine />
                </span>
                <span>{isViewMode ? "Edit" : "View"}</span>
              </button>
            </div>
          )}

          <a
            href={qrValue}
            className='addrec-qr-link'
            target='_blank'
            rel='noreferrer'
          >
            Click Here
          </a>

          <div className='addrec-qr-card'  >
            <div className='addrec-qr-head'>
              <div className='addrec-qr-title'>QR Code</div>
              <div className='addrec-qr-sub'>
                Scan with your phone’s camera.
              </div>
            </div>

            <div className='addrec-qr-box' style={{ marginRight: "1px" }}>
              <div
                style={{
                  display: "flex",
                  justifyContent: "center",
                  alignItems: "center",
                  width: "100%",


                }}
              >
                <QRCode
                  size={160}
                  value={qrValue}
                  style={{
                    height: "auto",
                    width: "160px",
                  }}
                  viewBox={`0 0 300 300`}
                />
              </div>
            </div>

            <div className='addrec-qr-download'>
              <VscCloudDownload className='addrec-qr-download-icon' />
              <div className='addrec-qr-dl-text'>
                <div className='addrec-qr-dl-main'>Download</div>
                <div className='addrec-qr-dl-sub'>QR</div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AddRecruitment;
